import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { istAktiv } from '../../lib/rbac/navigationAktiv';
import { useAuth } from '../auth/use-auth';
import { usePermissions } from '../rbac/use-permission';
import { deriveNavigation, type NavNode } from '../../lib/rbac/navigation';
import { pagesForNode } from '../../lib/rbac/pages';
import { Logo } from '../ui/logo';
import { Button } from '../ui/button';
import {
  Home, Users, Calendar, CheckSquare, Building, BarChart, LogOut, LayoutGrid, Mail,
  Music, Database, CreditCard, Globe, PanelLeftClose, PanelLeftOpen, Megaphone, ShieldCheck,
  UserCog, Briefcase, Layers, Settings, ArrowLeftRight, FileX,
} from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';

// Die Sidebar ist abgeleitet (RBAC-SPEC D.9, Teil E): welche Menüpunkte erscheinen,
// entscheidet deriveNavigation() aus den Permissions des Servers; unter jedem Punkt
// hängen die Seiten, die der Benutzer laut Seitentabelle öffnen darf. Keine Rollen.

const NODE_ICONS: Record<string, React.ReactNode> = {
  dashboard: <Home size={18} />,
  arbeitsbereiche: <CheckSquare size={18} />,
  aufgaben: <CheckSquare size={16} />,
  kalender: <Calendar size={16} />,
  anfragen: <Mail size={16} />,
  anmeldung: <Music size={16} />,
  migrationen: <Database size={16} />,
  'personen-bereich': <Users size={18} />,
  personen: <Users size={16} />,
  personenlisten: <Layers size={16} />,
  unterricht: <Calendar size={18} />,
  termine: <Calendar size={16} />,
  raumplan: <LayoutGrid size={16} />,
  lehrerwechsel: <ArrowLeftRight size={16} />,
  kuendigungen: <FileX size={16} />,
  finanzen: <CreditCard size={18} />,
  recruiting: <Briefcase size={18} />,
  marketing: <Megaphone size={18} />,
  website: <Globe size={18} />,
  auswertungen: <BarChart size={18} />,
  administration: <Settings size={18} />,
};
const iconFor = (node: NavNode) =>
  NODE_ICONS[node.id] ?? (node.id.startsWith('standort-') ? <Building size={16} /> : node.id.startsWith('admin-') ? <ShieldCheck size={16} /> : <Database size={16} />);

interface SidebarItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  collapsed?: boolean;
}
const SidebarItem = ({ to, icon, label, active, collapsed = false }: SidebarItemProps) => {
  const content = (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative flex items-center rounded-md border border-transparent text-sm font-medium transition-all duration-fast ease-standard',
        collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5',
        active
          ? 'border-primary/30 bg-primary/12 text-foreground shadow-xs'
          : 'text-muted-foreground hover:border-border/60 hover:bg-muted hover:text-foreground',
      )}
    >
      {!collapsed && (
        <span
          className={cn(
            'absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r-full transition-all duration-fast',
            active ? 'bg-primary opacity-100' : 'bg-border opacity-0 group-hover:opacity-70',
          )}
        />
      )}
      <span className={cn('inline-flex h-5 w-5 items-center justify-center', active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')}>
        {icon}
      </span>
      {!collapsed && <span>{label}</span>}
    </Link>
  );
  if (!collapsed) return content;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
};

interface SidebarGroupProps {
  value: string;
  icon: React.ReactNode;
  label: string;
  defaultOpen: boolean;
  children: React.ReactNode;
}
const SidebarGroup = ({ value, icon, label, defaultOpen, children }: SidebarGroupProps) => (
  <Accordion type="multiple" defaultValue={defaultOpen ? [value] : []} className="w-full">
    <AccordionItem value={value} className="border-none">
      <AccordionTrigger className="rounded-md border border-transparent px-3 py-2.5 text-sm font-medium hover:border-border/60 hover:bg-muted">
        <div className="flex items-center gap-3">
          {icon}
          <span>{label}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="ml-6 space-y-1">{children}</div>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

interface SidebarProps {
  collapsed?: boolean;
  showToggle?: boolean;
  onToggleCollapse?: () => void;
}
export const Sidebar = ({ collapsed = false, showToggle = false, onToggleCollapse }: SidebarProps) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { status, permissions, canAny } = usePermissions();
  const navigation = useMemo(() => deriveNavigation(permissions), [permissions]);

  // Seiten eines Menüpunkts, die der Benutzer öffnen darf; ohne Seite der leere Bereich.
  const linksFor = (node: NavNode) => {
    const links = pagesForNode(node.id).filter((page) => canAny(page.anyOf));
    return links.length > 0 ? links : [{ path: `/dashboard/bereich/${node.id}`, label: node.label, anyOf: [] }];
  };

  // Alle Pfade des Menüs. Daran misst sich, ob es zur Adresse einen genaueren
  // Punkt gibt — sonst leuchteten „Tickets" und „Testdaten" gleichzeitig.
  const allePfade = useMemo(
    () =>
      navigation.flatMap((node) =>
        [node, ...(node.children ?? [])].flatMap((n) => linksFor(n).map((link) => link.path))
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigation, permissions]
  );

  const isActive = (path: string) => istAktiv(path, location.pathname, allePfade);
  const nodeActive = (node: NavNode): boolean =>
    [node, ...(node.children ?? [])].some((n) => linksFor(n).some((link) => isActive(link.path)));

  const renderLeaf = (node: NavNode, depth: 1 | 2) => {
    const links = linksFor(node);
    if (collapsed || links.length === 1) {
      return (
        <SidebarItem
          key={node.id}
          to={links[0].path}
          icon={iconFor(node)}
          label={links.length === 1 ? links[0].label : node.label}
          active={links.some((link) => isActive(link.path))}
          collapsed={collapsed}
        />
      );
    }
    const item = (link: (typeof links)[number]) => (
      <SidebarItem key={link.path} to={link.path} icon={iconFor(node)} label={link.label} active={isActive(link.path)} />
    );
    // Seiten mit `gruppe` stehen unter einer Zwischenüberschrift, in der Reihenfolge
    // ihres ersten Auftretens; Seiten ohne Gruppe bleiben oben.
    const gruppen = [...new Set(links.flatMap((link) => (link.gruppe ? [link.gruppe] : [])))];
    const items =
      gruppen.length === 0
        ? links.map(item)
        : [
            ...links.filter((link) => !link.gruppe).map(item),
            ...gruppen.map((gruppe) => (
              <div key={gruppe} className="space-y-1">
                <p className="px-3 pt-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{gruppe}</p>
                {links.filter((link) => link.gruppe === gruppe).map(item)}
              </div>
            )),
          ];
    if (depth === 2) {
      return (
        <div key={node.id} className="space-y-1">
          <p className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{node.label}</p>
          {items}
        </div>
      );
    }
    return (
      <SidebarGroup key={node.id} value={node.id} icon={iconFor(node)} label={node.label} defaultOpen={nodeActive(node)}>
        {items}
      </SidebarGroup>
    );
  };

  const renderNode = (node: NavNode) => {
    if (!node.children) return renderLeaf(node, 1);
    if (collapsed) {
      const first = linksFor(node.children[0])[0];
      return <SidebarItem key={node.id} to={first.path} icon={iconFor(node)} label={node.label} active={nodeActive(node)} collapsed />;
    }
    return (
      <SidebarGroup key={node.id} value={node.id} icon={iconFor(node)} label={node.label} defaultOpen={nodeActive(node)}>
        {node.children.map((child, index) =>
          child.trennerDavor && index > 0 ? (
            <React.Fragment key={child.id}>
              <div role="separator" className="mx-3 my-1 border-t border-border/70" />
              {renderLeaf(child, 2)}
            </React.Fragment>
          ) : (
            renderLeaf(child, 2)
          )
        )}
      </SidebarGroup>
    );
  };

  return (
    <TooltipProvider>
      <div className={cn('chrome-glass flex h-full min-h-0 flex-col transition-[width] duration-normal ease-standard', collapsed ? 'w-[76px]' : 'w-64')}>
        {/* Logo */}
        <div className={cn('flex h-16 shrink-0 items-center border-b border-border/70', collapsed ? 'justify-center px-2' : 'justify-between px-4')}>
          <Link to="/" className="flex items-center gap-2 overflow-hidden">
            <Logo size="sm" showText={!collapsed} />
          </Link>
          {showToggle && onToggleCollapse && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen'} onClick={onToggleCollapse} className={cn(collapsed && 'hidden')}>
                  <PanelLeftClose className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Navigation einklappen</TooltipContent>
            </Tooltip>
          )}
          {showToggle && onToggleCollapse && collapsed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Sidebar ausklappen" onClick={onToggleCollapse}>
                  <PanelLeftOpen className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Navigation ausklappen</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Navigation */}
        <div className="relative min-h-0 flex-1">
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-background/90 to-transparent" />
          <nav aria-label="Hauptnavigation" className="h-full space-y-1 overflow-y-auto overscroll-contain px-3 py-4 [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border/80 [&::-webkit-scrollbar]:w-2">
            {status === 'loading' && (
              <div className="space-y-2 px-3" role="status" aria-label="Navigation wird geladen">
                {[0, 1, 2, 3].map((i) => <div key={i} className="h-8 animate-pulse rounded-md bg-muted/60" />)}
              </div>
            )}
            {status !== 'loading' && navigation.length === 0 && !collapsed && (
              <p className="px-3 text-sm text-muted-foreground">Für dein Konto sind keine Bereiche freigegeben.</p>
            )}
            {status !== 'loading' && navigation.map(renderNode)}
          </nav>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-6 bg-gradient-to-t from-background/90 to-transparent" />
        </div>

        {/* User info and logout */}
        <div className="shrink-0 border-t border-border/70 p-4">
          <div className={cn('mb-3', collapsed ? 'flex justify-center' : 'block')}>
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button asChild variant={isActive('/dashboard/account-security') ? 'secondary' : 'ghost'} size="icon" aria-label="Mein Konto">
                    <Link to="/dashboard/account-security">
                      <UserCog size={18} />
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Mein Konto</TooltipContent>
              </Tooltip>
            ) : (
              <Link
                to="/dashboard/account-security"
                className={cn(
                  'group relative flex items-center gap-3 rounded-md border border-transparent px-3 py-2.5 text-sm font-medium transition-all duration-fast ease-standard',
                  isActive('/dashboard/account-security')
                    ? 'border-primary/30 bg-primary/12 text-foreground shadow-xs'
                    : 'text-muted-foreground hover:border-border/60 hover:bg-muted hover:text-foreground',
                )}
              >
                <span className={cn('inline-flex h-5 w-5 items-center justify-center', isActive('/dashboard/account-security') ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')}>
                  <UserCog size={18} />
                </span>
                <span>Mein Konto</span>
              </Link>
            )}
          </div>

          <div className={cn('flex items-center', collapsed ? 'justify-center' : 'justify-between')}>
            <div className={cn('flex min-w-0 items-center gap-3', collapsed && 'hidden')}>
              <div className="h-8 w-8 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                {user?.name.charAt(0)}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{user?.name}</p>
                <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button onClick={logout} variant="ghost" size="icon" aria-label="Abmelden">
                    <LogOut size={18} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">{user?.name || 'Abmelden'}</TooltipContent>
              </Tooltip>
            ) : (
              <Button onClick={logout} variant="ghost" size="icon" aria-label="Abmelden">
                <LogOut size={18} />
              </Button>
            )}
          </div>

          <BuildInfoFooter collapsed={collapsed} />
        </div>
      </div>
    </TooltipProvider>
  );
};

/**
 * Kleine Footer-Anzeige am Ende der Sidebar mit Version + Commit + Build-Zeit.
 * Die Werte werden zur Build-Zeit von Vite injiziert (siehe vite.config.ts).
 */
const BuildInfoFooter = ({ collapsed }: { collapsed: boolean }) => {
  const version = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0';
  const commit = typeof __APP_COMMIT__ === 'string' ? __APP_COMMIT__ : 'unknown';
  const builtAt = typeof __APP_BUILT_AT__ === 'string' ? __APP_BUILT_AT__ : '';
  const builtAtLabel = (() => {
    if (!builtAt) return '';
    const d = new Date(builtAt);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  })();
  const tooltipLabel = `Version ${version} · ${commit}${builtAtLabel ? ` · ${builtAtLabel}` : ''}`;

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="mt-1 text-center text-[10px] font-mono text-muted-foreground/70 select-none">
            v{version}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">{tooltipLabel}</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="mt-2 flex items-center justify-between gap-2 px-1 text-[10px] font-mono text-muted-foreground/70 select-none">
          <span>v{version}</span>
          <span className="truncate">{commit}</span>
          {builtAtLabel ? <span className="truncate">{builtAtLabel}</span> : null}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top">{tooltipLabel}</TooltipContent>
    </Tooltip>
  );
};
